import {
  Alert,
  Button,
  Dialog,
  Divider,
  OpenInNewIcon,
  Stack,
  Tag,
  Typography,
} from '@all-manager/design-system-v2';
import { type Props, useDocumentIndexingErrorModal } from './useDocumentIndexingErrorModal';

export default function DocumentIndexingErrorModal(props: Props) {
  const hook = useDocumentIndexingErrorModal(props);

  if (!props.open || !hook.item) return null;

  const isUnsupported = hook.item.status === 'unsupported';

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title={
        <Stack spacing={0.5}>
          <Typography variant="h3">{hook.item.name}</Typography>
          <Typography variant="body2">
            {isUnsupported ? 'Document non pris en charge' : "Détails de l'erreur d'indexation"}
          </Typography>
        </Stack>
      }
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Fermer',
          onClick: props.onClose,
        },
        secondaryAction: hook.item.fileUrl
          ? {
              label: "Ouvrir l'original",
              onClick: hook.handleOpenOriginal,
            }
          : undefined,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
          <Tag
            color={isUnsupported ? 'default' : 'cancelled'}
            variantIcon="state"
            label={isUnsupported ? 'Non pris en charge' : 'Erreur'}
          />
          {hook.item.documentTypeName ? (
            <Tag color="default" label={hook.item.documentTypeName} />
          ) : null}
          {!isUnsupported && hook.item.attempts > 0 ? (
            <Tag
              color={hook.isMaxAttemptsReached ? 'cancelled' : 'pending'}
              label={`${hook.item.attempts} tentative${hook.item.attempts > 1 ? 's' : ''} / 5`}
            />
          ) : null}
        </Stack>

        <Divider />

        <Alert severity={isUnsupported ? 'warning' : 'error'}>
          <Typography variant="body2Emphasis">
            {isUnsupported ? 'Raison :' : 'Message renvoyé :'}
          </Typography>
          <Typography variant="body2">
            {hook.item.error || 'Une erreur inconnue est survenue lors de la tentative de lecture du document.'}
          </Typography>
        </Alert>

        {hook.isDriveDisconnected ? (
          <Alert severity="info">
            Google Drive semble déconnecté pour cette copropriété. Rendez-vous dans l'onglet « Google Drive » des paramètres pour rétablir la connexion.
          </Alert>
        ) : null}

        {hook.formattedNextAttempt && !hook.isMaxAttemptsReached ? (
          <Stack spacing={0.5}>
            <Typography variant="body2Emphasis">Prochaine tentative automatique :</Typography>
            <Typography variant="body2">{hook.formattedNextAttempt}</Typography>
          </Stack>
        ) : null}

        {hook.isMaxAttemptsReached ? (
          <Typography variant="body2">
            Le nombre maximal de 5 tentatives a été atteint pour ce fichier. L'indexation ne sera relancée automatiquement que si le fichier est remplacé ou republié.
          </Typography>
        ) : null}

        {hook.item.fileUrl ? (
          <Stack direction="row" justifyContent="flex-start">
            <Button
              variant="text"
              size="small"
              color="default"
              startIcon={<OpenInNewIcon />}
              onClick={hook.handleOpenOriginal}
            >
              Voir le fichier d'origine
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Dialog>
  );
}

